import { action, observable, makeObservable } from 'mobx';
import { Model, IModelAttributes, attribute, entity } from 'Models/Model';
import { isNotNullOrUndefined } from 'Util/TypeGuards';
import * as Models from 'Models/Entities';

export interface IParkingLocationEntityAttributes extends IModelAttributes {
	name: string;
	address?: string;
	disabled?: boolean;
	maxCapacity: number;
	instructions?: string;
	parkingTermsAndConditions?: string;
	locationId?: string;
	location?: Models.LocationEntity | Models.ILocationEntityAttributes;
}

@entity('ParkingLocationEntity', 'Parking Location')
export default class ParkingLocationEntity extends Model implements IParkingLocationEntityAttributes {
	/**
	 * Fields to exclude from the JSON serialization in create operations.
	 */
	public static excludeFromCreate: string[] = [];

	/**
	 * Fields to exclude from the JSON serialization in update operations.
	 */
	public static excludeFromUpdate: string[] = [];

	@observable
	@attribute<ParkingLocationEntity, string>()
	public name: string;

	@observable
	@attribute<ParkingLocationEntity, string>()
	public address?: string;

	@observable
	@attribute<ParkingLocationEntity, boolean>()
	public disabled?: boolean;

	@observable
	@attribute<ParkingLocationEntity, number>()
	public maxCapacity: number;

	@observable
	@attribute<ParkingLocationEntity, string>()
	public instructions?: string;

	@observable
	@attribute<ParkingLocationEntity, string>()
	public parkingTermsAndConditions?: string;

	@observable
	@attribute()
	public locationId?: string;

	@observable
	@attribute({ isReference: true, manyReference: false })
	public location?: Models.LocationEntity;

	constructor(attributes?: Partial<IParkingLocationEntityAttributes>) {
		super(attributes);

		makeObservable(this);

		this.assignAttributes(attributes);
	}

	/**
	 * Assigns fields from a passed in JSON object to the fields in this model.
	 * Any reference objects that are passed in are converted to models if they are not already.
	 * This function is called from the constructor to assign the initial fields.
	 */
	public assignAttributes(attributes?: Partial<IParkingLocationEntityAttributes>) {
		super.assignAttributes(attributes);

		if (attributes) {
			if (isNotNullOrUndefined(attributes.name)) {
				this.name = attributes.name;
			}
			if (isNotNullOrUndefined(attributes.address)) {
				this.address = attributes.address;
			}
			if (isNotNullOrUndefined(attributes.disabled)) {
				this.disabled = attributes.disabled;
			}
			if (isNotNullOrUndefined(attributes.maxCapacity)) {
				this.maxCapacity = Number(attributes.maxCapacity);
			}
			if (isNotNullOrUndefined(attributes.instructions)) {
				this.instructions = attributes.instructions;
			}
			if (isNotNullOrUndefined(attributes.parkingTermsAndConditions)) {
				this.parkingTermsAndConditions = attributes.parkingTermsAndConditions;
			}
			if (isNotNullOrUndefined(attributes.locationId)) {
				this.locationId = attributes.locationId;
			}
			if (isNotNullOrUndefined(attributes.location)) {
				if (attributes.location === null) {
					this.location = attributes.location;
				} else if (attributes.location instanceof Models.LocationEntity) {
					this.location = attributes.location;
					this.locationId = attributes.location.id;
				} else {
					this.location = new Models.LocationEntity(attributes.location);
					this.locationId = this.location.id;
				}
			}
		}
	}
}
